import { DraftSectionItem } from '../../../components/Section/Section.model'
import {
  DraftConjointItem,
  DraftEntryResponseOption,
  DraftTextHighlighterItem
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  DraftTextCardItem,
  EntryType,
  MaxDiffLabelTranslation
} from '../../../data/model/questionnaire'

/**
 * Port of cin-questionnaire's `LocaleTranslationDomainV2` resolver. The backend
 * applies it only on the respondent read; the editor read (which the export
 * uses) returns the shared rows plus every country's raw overrides, so a
 * market document has to collapse its country's overrides itself:
 *   - an English override (the master's language) replaces the master text,
 *     and the shared English row if there is one, but never adds that row;
 *   - any other language replaces that language's shared row, or adds one.
 * Run it before `applyTranslationOverlay`, which then reads the shared rows.
 */

const MASTER_LANGUAGE = 'en'

type Overrides<T> =
  | ReadonlyArray<{ country: string; translation: T }>
  | null
  | undefined

interface Resolved<T> {
  rows: T[] | null | undefined
  master: T | undefined
}

const resolveRows = <T>(
  shared: T[] | null | undefined,
  overrides: Overrides<T>,
  country: string,
  key: (row: T) => string,
  language: (row: T) => string
): Resolved<T> =>
  (overrides ?? [])
    .filter((override) => override.country === country)
    .reduce<Resolved<T>>(
      ({ rows, master }, { translation }) => {
        const isMaster = language(translation) === MASTER_LANGUAGE
        const existing = rows ?? []
        const index = existing.findIndex((row) => key(row) === key(translation))
        const next =
          index !== -1
            ? existing.map((row, i) => (i === index ? translation : row))
            : isMaster
              ? rows
              : [...existing, translation]
        return { rows: next, master: isMaster ? translation : master }
      },
      { rows: shared, master: undefined }
    )

const byLanguage = <O, T extends { language: string }>(
  owner: O,
  shared: T[] | null | undefined,
  overrides: Overrides<T>,
  country: string,
  rebuild: (rows: T[] | null | undefined, master: T | undefined) => O
): O => {
  if (!overrides?.length) return owner
  const { rows, master } = resolveRows(
    shared,
    overrides,
    country,
    (row) => row.language,
    (row) => row.language
  )
  return rebuild(rows, master)
}

interface TextRow {
  language: string
  text: string
  textStyling?: string | null
}

interface QuestionLike {
  text: string
  textStyling?: string | null
  translations?: TextRow[] | null
  localeTranslations?: Overrides<TextRow>
}

const resolveQuestion = <Q extends QuestionLike>(
  question: Q | null | undefined,
  country: string
): Q | null | undefined =>
  question &&
  byLanguage(
    question,
    question.translations,
    question.localeTranslations,
    country,
    (rows, master) => ({
      ...question,
      ...(master && { text: master.text, textStyling: master.textStyling }),
      translations: rows,
      localeTranslations: null
    })
  )

interface ValueRow {
  language: string
  value: string
}

interface ResponseOptionLike {
  value: string
  translations?: ValueRow[] | null
  localeTranslations?: Overrides<ValueRow>
}

const resolveResponseOptionOverrides = <R extends ResponseOptionLike>(
  option: R,
  country: string
): R =>
  byLanguage(
    option,
    option.translations,
    option.localeTranslations,
    country,
    (rows, master) => ({
      ...option,
      ...(master && { value: master.value }),
      translations: rows,
      localeTranslations: null
    })
  )

const resolveEntryResponseOption = (
  option: DraftEntryResponseOption,
  country: string
): DraftEntryResponseOption => ({
  ...option,
  responseOption:
    option.responseOption &&
    resolveResponseOptionOverrides(option.responseOption, country)
})

// MaxDiff labels are optional one by one: a label the override leaves out
// falls back to the shared label in that language, then to the master.
const resolveMaxDiffLabels = (
  item: DraftQuestionItem,
  country: string
): DraftQuestionItem => {
  const withSharedLabels = (
    t: MaxDiffLabelTranslation
  ): MaxDiffLabelTranslation => {
    const shared = item.maxDiffLabelTranslations?.find(
      (s) => s.language === t.language
    )
    return shared
      ? {
          ...t,
          mostLabel: t.mostLabel ?? shared.mostLabel,
          leastLabel: t.leastLabel ?? shared.leastLabel,
          attributeLabel: t.attributeLabel ?? shared.attributeLabel
        }
      : t
  }
  const overrides = item.localeMaxDiffLabelTranslations
    ?.filter((o) => o.country === country)
    .map((o) => ({ ...o, translation: withSharedLabels(o.translation) }))
  return byLanguage(
    item,
    item.maxDiffLabelTranslations,
    overrides,
    country,
    (rows, master) => {
      const spec = item.maxDiffSpecification
      const label = <L extends { text: string }>(
        current: L,
        text: string | null | undefined
      ): L => (text == null ? current : { ...current, text })
      return {
        ...item,
        maxDiffSpecification:
          master && spec
            ? {
                ...spec,
                mostLabel: label(spec.mostLabel, master.mostLabel),
                leastLabel: label(spec.leastLabel, master.leastLabel),
                attributeLabel: label(
                  spec.attributeLabel,
                  master.attributeLabel
                )
              }
            : spec,
        maxDiffLabelTranslations: rows,
        localeMaxDiffLabelTranslations: null
      }
    }
  )
}

const resolveQuestionItem = (
  item: DraftQuestionItem,
  country: string
): DraftQuestionItem => {
  const own = resolveMaxDiffLabels(item, country)
  return {
    ...own,
    question: resolveQuestion(own.question, country) ?? undefined,
    responseOptions: own.responseOptions.map((option) =>
      resolveEntryResponseOption(option, country)
    ),
    questionScale: own.questionScale && {
      ...own.questionScale,
      labels: own.questionScale.labels.map((label) =>
        byLanguage(
          label,
          label.labelTranslations,
          label.localeLabelTranslations,
          country,
          (rows, master) => ({
            ...label,
            label: master ? { ...label.label, text: master.text } : label.label,
            labelTranslations: rows ?? [],
            localeLabelTranslations: null
          })
        )
      )
    }
  }
}

const resolveMatrixItem = (
  item: DraftMatrixItem,
  country: string
): DraftMatrixItem => {
  const own = byLanguage(
    item,
    item.translations,
    item.localeTranslations,
    country,
    (rows, master) => ({
      ...item,
      matrixTitle:
        master && item.matrixTitle
          ? {
              ...item.matrixTitle,
              title: master.title,
              titleStyling: master.titleStyling ?? null
            }
          : item.matrixTitle,
      translations: rows,
      localeTranslations: null
    })
  )
  return {
    ...own,
    matrixRows: own.matrixRows.map((row) => ({
      ...row,
      question: resolveQuestion(row.question, country)
    })),
    responseOptions: own.responseOptions.map((option) =>
      resolveEntryResponseOption(option, country)
    )
  }
}

const resolveTextCardItem = (
  item: DraftTextCardItem,
  country: string
): DraftTextCardItem =>
  byLanguage(
    item,
    item.translations,
    item.localeTranslations,
    country,
    (rows, master) => ({
      ...item,
      textCard: master
        ? {
            ...item.textCard,
            title: master.title,
            subtitle: master.subtitle,
            body: master.body,
            titleStyling: master.titleStyling ?? null
          }
        : item.textCard,
      translations: rows,
      localeTranslations: null
    })
  )

const resolveHighlighterItem = (
  item: DraftTextHighlighterItem,
  country: string
): DraftTextHighlighterItem => {
  const own = byLanguage(
    item,
    item.translations,
    item.localeTranslations,
    country,
    (rows, master) => ({
      ...item,
      ...(master && {
        question: item.question && {
          ...item.question,
          text: master.text,
          textStyling: master.textStyling
        },
        content: master.content
      }),
      translations: rows,
      localeTranslations: null
    })
  )
  return {
    ...own,
    question: resolveQuestion(own.question, country),
    highlighterCategories: own.highlighterCategories.map((category) => ({
      ...category,
      responseOption: resolveResponseOptionOverrides(
        category.responseOption,
        country
      )
    }))
  }
}

const resolveConjointItem = (
  item: DraftConjointItem,
  country: string
): DraftConjointItem => {
  const withText = byLanguage(
    item,
    item.translations,
    item.localeTranslations,
    country,
    (rows, master) => ({
      ...item,
      ...(master && {
        question: item.question && {
          ...item.question,
          text: master.text,
          textStyling: master.textStyling
        }
      }),
      translations: rows,
      localeTranslations: null
    })
  )
  const own = {
    ...withText,
    question: resolveQuestion(withText.question, country)
  }
  if (!own.localeAttributeTranslations?.length) return own
  // Attribute rows are identified by (language, attribute), like the shared upsert.
  const { rows } = resolveRows(
    own.attributeTranslations,
    own.localeAttributeTranslations,
    country,
    (t) => `${t.language}\u0000${t.attributeId}`,
    (t) => t.language
  )
  // Later rows win, as in the backend's fold over its master-language rows.
  const toMaster = new Map(
    own.localeAttributeTranslations
      .filter(
        (o) =>
          o.country === country && o.translation.language === MASTER_LANGUAGE
      )
      .map((o) => [o.translation.attributeId, o.translation])
  )
  return {
    ...own,
    attributes: own.attributes.map((attribute) => {
      const t = toMaster.get(attribute.attributeId)
      return t
        ? {
            ...attribute,
            name: t.name,
            options: attribute.options.map((option) => {
              const value = t.options.find(
                (o) => o.attributeOptionId === option.attributeOptionId
              )?.value
              return value === undefined ? option : { ...option, value }
            })
          }
        : attribute
    }),
    attributeTranslations: rows,
    localeAttributeTranslations: null
  }
}

/**
 * The entry as a market in `country` renders it: that country's overrides
 * folded into the shared rows and the master text. No country, no change.
 */
export const resolveLocaleOverrides = (
  entry: DraftQuestionnaireEntry,
  country: string | undefined
): DraftQuestionnaireEntry => {
  if (!country) return entry
  switch (entry.entryType) {
    case EntryType.SectionEntryType: {
      const item = entry.entryItem as DraftSectionItem
      const own = byLanguage(
        item,
        item.translations,
        item.localeTranslations,
        country,
        (rows, master) => ({
          ...item,
          title: master ? master.title : item.title,
          translations: rows,
          localeTranslations: null
        })
      )
      return {
        ...entry,
        entryItem: {
          ...own,
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          entries: (own.entries ?? []).map((child) =>
            resolveLocaleOverrides(child, country)
          )
        }
      } as DraftQuestionnaireEntry
    }
    case EntryType.QuestionEntryType:
      return {
        ...entry,
        entryItem: resolveQuestionItem(
          entry.entryItem as DraftQuestionItem,
          country
        )
      } as DraftQuestionnaireEntry
    case EntryType.MatrixEntryType:
      return {
        ...entry,
        entryItem: resolveMatrixItem(
          entry.entryItem as DraftMatrixItem,
          country
        )
      } as DraftQuestionnaireEntry
    case EntryType.TextCardEntryType:
      return {
        ...entry,
        entryItem: resolveTextCardItem(
          entry.entryItem as DraftTextCardItem,
          country
        )
      } as DraftQuestionnaireEntry
    case EntryType.TextHighlighterEntryType:
      return {
        ...entry,
        entryItem: resolveHighlighterItem(
          entry.entryItem as DraftTextHighlighterItem,
          country
        )
      } as DraftQuestionnaireEntry
    case EntryType.ConjointEntryType:
      return {
        ...entry,
        entryItem: resolveConjointItem(
          entry.entryItem as DraftConjointItem,
          country
        )
      } as DraftQuestionnaireEntry
    default:
      return entry
  }
}
